/**
 * DecisionGuideView — queue #667 §UAT-2026-07-09-R667 §R667-4.
 *
 * 전역 '선택 가이드' 뷰 기본 화면 — 6개 비교군 카드 목록(제목 + 1줄 요약 + 후보 코드
 * 배지) + 선택된 군의 비교표(`DecisionTable`). 카드 클릭 시 호출부(CommonShowcasePage)가
 * `?guide=<id>` 로 선택을 URL 에 동기화한다(§R640 URL-as-SSOT 계보 · §R664 RecipeGallery
 * 카드 패턴 답습). 정적 import 데이터라 로딩 상태가 없다(§R667-4).
 */
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@ui-kit';
import type { DecisionGuide } from '../data/decision-guides';
import { guideOptionCodes } from '../data/decision-guides';
import type { ModuleNode } from '../types/showcase';
import { DecisionTable } from './DecisionTable';

interface Props {
  guides: DecisionGuide[];
  /** 성숙도 파생용 카탈로그 노드(DecisionTable 로 그대로 전달) */
  nodes: ModuleNode[];
  /** `?guide=<id>` — 선택된 비교군(없으면 안내 문구만) */
  selectedGuideId: string | null;
  /** `?highlight=<code>` — 모듈 상세 '대안과 비교' 진입점 딥링크로 강조할 후보 */
  highlightCode?: string | null;
  onSelectGuide: (id: string) => void;
  onSelectModule: (code: string) => void;
  onSelectRecipe: (id: string) => void;
  onSelectPitfall: (id: string) => void;
}

export function DecisionGuideView({
  guides,
  nodes,
  selectedGuideId,
  highlightCode,
  onSelectGuide,
  onSelectModule,
  onSelectRecipe,
  onSelectPitfall,
}: Props) {
  const { t } = useTranslation();

  // §R667-4 empty — 6군 상시 존재하나 shape 방어로 0건이면 안내 문구(뷰 미중단).
  if (!guides || guides.length === 0) {
    return (
      <section aria-label={t('showcase.decision.gallery.ariaLabel')}>
        <EmptyState title={t('showcase.decision.gallery.hint')} />
      </section>
    );
  }

  const selectedGuide = selectedGuideId ? guides.find((g) => g.id === selectedGuideId) ?? null : null;

  return (
    <section aria-label={t('showcase.decision.gallery.ariaLabel')}>
      <h2
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-1) 0',
        }}
      >
        {t('showcase.decision.title')}
      </h2>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          margin: '0 0 var(--spacing-4) 0',
        }}
      >
        {t('showcase.decision.subtitle')}
      </p>

      <div
        role="group"
        aria-label={t('showcase.decision.gallery.ariaLabel')}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        {guides.map((guide) => {
          const isSelected = guide.id === selectedGuideId;
          return (
            <button
              key={guide.id}
              type="button"
              data-testid={`decision-guide-card-${guide.id}`}
              aria-pressed={isSelected}
              onClick={() => onSelectGuide(guide.id)}
              style={{
                textAlign: 'left',
                padding: 'var(--spacing-3) var(--spacing-4)',
                border: isSelected ? '2px solid var(--color-brand-primary)' : '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                background: isSelected ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-2)',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 700,
                  color: 'var(--color-text-primary)',
                }}
              >
                {t(guide.titleKey)}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {t(guide.summaryKey)}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)' }}>
                {guideOptionCodes(guide).map((code) => (
                  <code
                    key={code}
                    style={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      background: 'var(--color-surface-elevated)',
                      border: '1px solid var(--color-surface-border)',
                      padding: '2px var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--color-brand-primary)',
                    }}
                  >
                    {code}
                  </code>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {selectedGuide ? (
        <DecisionTable
          guide={selectedGuide}
          nodes={nodes}
          highlightCode={highlightCode}
          onSelectModule={onSelectModule}
          onSelectRecipe={onSelectRecipe}
          onSelectPitfall={onSelectPitfall}
        />
      ) : (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
          {t('showcase.decision.gallery.hint')}
        </p>
      )}
    </section>
  );
}
